import React from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { Typography, TypographyTypeEnum } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./AlertDialog.module.scss";

export interface AlertDialogContentComponentProps
  extends AlertDialogPrimitive.AlertDialogContentProps,
    React.RefAttributes<HTMLDivElement> {
  title: string;
  description: React.ReactNode;
  actions: React.ReactNode;
}

const COMPONENT_KEY = CoreUiComponents.AlertDialog;

export const AlertDialogContent = React.forwardRef<
  HTMLDivElement,
  AlertDialogContentComponentProps
>(({ children, ...props }, forwardedRef) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { title, className, description, actions } = props;

  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay className={clsx(cn("overlay"))} />
      <AlertDialogPrimitive.Content
        ref={forwardedRef}
        className={clsx(cn("root"), className)}
      >
        <AlertDialogPrimitive.Title className={cn("title")}>
          <Typography type={TypographyTypeEnum.Heading} size="xs">
            {title}
          </Typography>
        </AlertDialogPrimitive.Title>

        <div className={cn("content")}>
          <AlertDialogPrimitive.Description className={cn("description")}>
            {description}
          </AlertDialogPrimitive.Description>
          <div className={cn("actions")}>{actions}</div>
          {children}
        </div>
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  );
});

export type AlertDialogProps = AlertDialogPrimitive.AlertDialogProps & {
  onOpen?: () => void;
  onClose?: () => void;
};

export const AlertDialog = ({
  onOpen,
  onClose,
  ...props
}: AlertDialogProps) => {
  const handleOpenChange = (isOpen: boolean) => {
    isOpen ? onOpen?.() : onClose?.();

    if (props.onOpenChange) {
      props.onOpenChange(isOpen);
    }
  };

  return (
    <AlertDialogPrimitive.Root {...props} onOpenChange={handleOpenChange} />
  );
};

export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
export const AlertDialogCancel = AlertDialogPrimitive.Cancel;
export const AlertDialogAction = AlertDialogPrimitive.Action;
